import React, { useId } from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import styles from '../../containers/common/Common.module.css';

/**
 * The Dashboard entry's icon — four tiles that behave like a live dashboard instead of a
 * flat glyph, since it is the first row of the rail and the page most people land on.
 *
 * Drawn by hand rather than taken from @mui/icons-material because the animation is
 * per-tile: an MUI icon is a single <path> with nothing inside it to stagger. It is still an
 * MUI icon in every other respect — SvgIcon gives it the same sizing, colour inheritance and
 * `fontSize` prop as the icons on every other row, so `renderRow` and MobileMenu need no
 * special case for it.
 *
 * The motion lives in Common.module.css beside the rail's own rules rather than in `sx`,
 * because it is driven by the row's state: .menuButton:hover and .selectedMenuButton are
 * classes in that module, and a CSS-module class cannot be reached from another file. All of
 * it is switched off under prefers-reduced-motion.
 */
export default function DashboardGlyph({ className, ...props }) {
    // Three <defs> ids that must not collide: the rail and the mobile sheet can both have a
    // Dashboard icon mounted at once, and a duplicate id would make one of them pick up the
    // other's gradient. useId() hands back something like ":r3:", and the colons make the id
    // awkward to reference — url(#...) resolves them, but querySelector and CSS selectors do
    // not — so they come out.
    const uid = useId().replace(/:/g, '');
    const gradientId = `dashGradient-${uid}`;
    const sheenId = `dashSheen-${uid}`;
    const clipId = `dashClip-${uid}`;

    // Unequal tiles, like the MUI Dashboard icon this replaces: a 2×2 grid of identical
    // squares reads as "apps", not as a dashboard.
    const tiles = [
        { x: 3, y: 3, width: 8, height: 9 },
        { x: 13, y: 3, width: 8, height: 5 },
        { x: 13, y: 10, width: 8, height: 11 },
        { x: 3, y: 14, width: 8, height: 7 }
    ];

    return (
        <SvgIcon
            {...props}
            className={[styles.dashGlyph, className].filter(Boolean).join(' ')}
            viewBox="0 0 24 24">
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FF8701" />
                    <stop offset="100%" stopColor="#FFC100" />
                </linearGradient>
                {/* The sheen that crosses the tiles while the page is selected. */}
                <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="50%" stopColor="#fff" stopOpacity="0.75" />
                    <stop offset="100%" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
                {/* Keeps the gradient and the sheen inside the tiles rather than washing
                    over the gaps. Carries the same class and delays as the tiles themselves:
                    a clip that stayed put while the tiles popped on hover would let the
                    gradient show past their edges. */}
                <clipPath id={clipId}>
                    {tiles.map((tile, i) => (
                        <rect
                            key={i}
                            className={styles.dashTile}
                            {...tile}
                            rx="2.2"
                            style={{ animationDelay: `${i * 0.38}s`, transitionDelay: `${i * 0.045}s` }}
                        />
                    ))}
                </clipPath>
            </defs>

            <g>
                {tiles.map((tile, i) => (
                    <rect
                        key={i}
                        className={styles.dashTile}
                        {...tile}
                        rx="2.2"
                        fill="currentColor"
                        // Staggered by hand: a CSS rule per tile would be four near-identical
                        // blocks where the delay is the only thing that differs.
                        style={{ animationDelay: `${i * 0.38}s`, transitionDelay: `${i * 0.045}s` }}
                    />
                ))}
            </g>

            {/* Selected only — the gradient wash and the sheen are faded in by CSS. */}
            <g className={styles.dashAccent} clipPath={`url(#${clipId})`}>
                <rect x="0" y="0" width="24" height="24" fill={`url(#${gradientId})`} />
                <rect
                    className={styles.dashSheen}
                    x="-16"
                    y="-4"
                    width="12"
                    height="32"
                    fill={`url(#${sheenId})`}
                />
            </g>
        </SvgIcon>
    );
}
